import {
  announce as announceWithReactAria,
  destroyAnnouncer,
} from '@react-aria/live-announcer';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

export type AnnouncementPriority = 'polite' | 'assertive';

interface AnnouncerValue {
  announce(message: string, priority?: AnnouncementPriority): void;
}

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

export function AnnouncerProvider({ children }: PropsWithChildren) {
  const announce = useCallback((message: string, priority: AnnouncementPriority = 'polite') => {
    const text = message.trim();
    if (text) announceWithReactAria(text, priority);
  }, []);

  // Se crea la región vacía al arrancar. Además de asegurar que exista antes
  // del primer cambio (requisito de los lectores), evita que el retraso de
  // compatibilidad del primer anuncio invierta dos mensajes muy seguidos.
  // React Aria mantiene una sola pareja global: cortés y asertiva.
  useEffect(() => {
    announceWithReactAria('', 'polite');
    return () => destroyAnnouncer();
  }, []);

  const value = useMemo(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  const context = useContext(AnnouncerContext);
  if (!context) throw new Error('useAnnouncer debe utilizarse dentro de AnnouncerProvider.');
  return context;
}
